import {
  AddOutline18Icon,
  CloseOutline18Icon,
  StarsOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { FC, ReactNode, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionItemContent,
  AccordionItemTrigger,
  Flex,
  Text,
  tokens,
} from "shared/components/Spor";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

type ActionCardProps = {
  title: ReactNode;
  icon?: ReactNode;
  children?: ReactNode;
  primaryAction?: ReactNode;
  tertiaryAction?: ReactNode;
  defaultExpanded?: boolean;
  className?: string;
};

const Card = styled(Accordion)`
  width: 100%;
`;

const CardItem = styled(AccordionItem)<{ $expanded: boolean }>`
  overflow: hidden;
  color: ${({ theme }) => theme.colorTextMain};
  background: ${({ theme }) => theme.colorActionCardBackground};
  border: ${tokens.default.size.stroke.sm} solid;
  border-color: ${({ $expanded }) =>
    $expanded ? "var(--spor-colors-outline-disabled)" : "transparent"};

  &&& {
    outline: none;
  }

  &&&& .spor-accordion__itemBody {
    border-top-left-radius: ${tokens.default.size["border-radius"].none};
    border-top-right-radius: ${tokens.default.size["border-radius"].none};
    margin-top: 0;
  }
`;

const CardHeaderRow = styled.div<{ $expanded: boolean }>`
  display: flex;
  align-items: center;
  border-radius: calc(
      ${tokens.default.size["border-radius"].sm} -
        ${tokens.default.size.stroke.sm}
    )
    calc(
      ${tokens.default.size["border-radius"].sm} -
        ${tokens.default.size.stroke.sm}
    )
    ${tokens.default.size["border-radius"].none}
    ${tokens.default.size["border-radius"].none};
  background: ${({ $expanded, theme }) => {
    if (theme.themeName === "light") {
      return $expanded ? "transparent" : "rgba(0, 0, 0, 0.05)";
    }
    return "transparent";
  }};

  /* REVIEW-SPOR: Recheck the full Accordion header hover surface when Spor's hover token matches the spec. */
  &:hover {
    background: ${({ theme }) =>
      theme.themeName === "light"
        ? "rgba(178, 223, 215, 1)"
        : "rgba(0, 69, 62, 1)"};
  }

  &&& > h3 {
    flex: 1;
  }
`;

const CardHeader = styled(AccordionItemTrigger)`
  &&& {
    min-height: 40px;
    gap: ${tokens.default.size.spacing[1.5]};
    padding: ${tokens.default.size.spacing[2]};
    color: ${({ theme }) => theme.colorTextMain};
    text-align: left;
  }

  &&&:hover,
  &&&:active {
    outline: none;
  }
`;

const CardToggle = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--spor-colors-surface-brand);
  color: var(--spor-colors-text-brand);

  svg {
    transform: ${({ $expanded }) => ($expanded ? "rotate(90deg)" : "none")};
  }
`;

const CardContent = styled(AccordionItemContent)<{ $expanded: boolean }>`
  overflow: hidden;
  padding: ${tokens.default.size.spacing[0]};
  border-radius: ${tokens.default.size["border-radius"].none};
  border-top: ${({ $expanded }) =>
    $expanded
      ? `${tokens.default.size.stroke.sm} solid var(--spor-colors-outline-disabled)`
      : "none"};
`;

const CardBody = styled.div`
  display: flex;
  min-height: 136px;
  flex-direction: column;
  gap: ${tokens.default.size.spacing[2]};
  padding: ${tokens.default.size.spacing[2]};
`;

export const ActionCard: FC<ActionCardProps> = ({
  title,
  icon = <StarsOutline24Icon aria-hidden="true" />,
  children,
  primaryAction,
  tertiaryAction,
  defaultExpanded = false,
  className,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Card
      className={className}
      collapsible
      lazyMount
      value={expanded ? ["action-card"] : []}
      onValueChange={({ value }) => setExpanded(value.includes("action-card"))}
      variant="core"
    >
      <CardItem value="action-card" $expanded={expanded}>
        <CardHeaderRow data-action-card-header $expanded={expanded}>
          <CardHeader
            headingLevel="h3"
            showChevron={false}
            aria-label={t(
              expanded ? "Lukk handlingskort" : "Åpne handlingskort",
            )}
          >
            <Flex alignItems="center" flex="1" gap="1.5">
              {icon}
              <Text as="span" variant="sm-label-bold">
                {title}
              </Text>
            </Flex>
            <CardToggle aria-hidden="true" $expanded={expanded}>
              {expanded ? (
                <CloseOutline18Icon aria-hidden="true" />
              ) : (
                <AddOutline18Icon aria-hidden="true" />
              )}
            </CardToggle>
          </CardHeader>
        </CardHeaderRow>
        <CardContent data-action-card-content $expanded={expanded}>
          <CardBody style={{ border: "none" }}>
            {children}
            {(tertiaryAction || primaryAction) && (
              <Flex
                alignItems="center"
                gap={tokens.default.size.spacing[1.5]}
                justifyContent="flex-end"
                mt="auto"
              >
                {tertiaryAction}
                {primaryAction}
              </Flex>
            )}
          </CardBody>
        </CardContent>
      </CardItem>
    </Card>
  );
};
